<script>
  import { onMount, onDestroy } from 'svelte';
  import { fade, scale } from 'svelte/transition';
  import { setSelectedPrompt, promptStore, savePrompt, removeSavedPrompt, loadSavedPrompts } from '../stores/promptStore';
  import { fetchSelectedEvidence, searchEvidence } from '../stores/evidenceStore';
  import { storyStore } from '../stores/storyStore.js';
  import { navigate } from "svelte-routing";
  import { apiService } from '../services/apiService';
  import { authStore } from '../stores/authStore.js';

  // Admin user check
  $: isAdmin = $authStore?.user?.isAdmin || false;
  
  // Add menu state for admin actions
  let showMenu = null; // Stores ID of prompt with open menu
  
  // Add delete confirmation state
  let deleteConfirmOpen = false;
  let promptToDelete = null;
  
  // Add edit modal state
  let updateModalOpen = false;
  let promptToUpdate = null;
  let updateForm = {
    bigQuestion: '',
    promptScript: '',
    topics: []
  };
  
  // Add click outside handler for closing menus
  function handleClickOutside(event) {
    if (showMenu !== null && !event.target.closest('.prompt-menu')) {
      showMenu = null;
    }
  }

  export let prompts = [];
  let loading = false;
  let selectedPrompt = null;
  let showDetails = false;
  let activeTab = 'browse';
  let browsedPrompts = [];
  let displayLimit = 20;
  let loadingMore = false;
  let browseLoading = false;
  let expandedPromptId = null;
  let modalPrompt = null;
  let browseSearchQuery = "";
  let originalBrowsedPrompts = [];
  let filteredPrompts = [];
  let displayedPrompts = [];
  let isSearching = false;
  let searchQuery = "";
  // let hasMorePrompts = false;
  let totalPrompts = 0;
  let hasSubmittedSearch = false;
  let currentStep = 0;
  let showSearchTips = false;
  const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  
  // Remove search type toggle and related variables
  let searchErrorMessage = '';
  let showSearchError = false;

  $: savedPrompts = $promptStore?.savedPrompts || [];

  // Add this for tracking expanded states
  let expandedPrompts = {};

  const steps = [
    {
      title: "Choose Your Question",
      description: "Browse through our curated questions or search for topics that interest you. Each question is designed to help you explore data in meaningful ways.",
      image: "/images/guide/step1.png"
    },
    {
      title: "Select Your Template",
      description: "Pick a template that best fits your story. Each template is designed for different types of data visualization and storytelling approaches.",
      image: "/images/guide/step2.png"
    },
    {
      title: "Add Your Data",
      description: "Upload your data or use our sample datasets. Our tool will help you organize and prepare your data for visualization.",
      image: "/images/guide/step3.png"
    },
    {
      title: "Create Visualizations",
      description: "Use our intuitive tools to create beautiful and informative visualizations that help tell your story.",
      image: "/images/guide/step4.png"
    },
    {
      title: "Tell Your Story",
      description: "Add your narrative, insights, and conclusions to create a compelling data story that engages your audience.",
      image: "/images/guide/step5.png"
    }
  ];

  let currentPage = 1; // Track the current page
let pageSize = 20; // Number of prompts per page
let hasMorePrompts = true; // Whether more prompts are available

let promptCache = {}; // Cache object to store fetched prompts by page
window.selectPrompt = selectPrompt;

async function loadInitialPrompts() {
  currentPage = 1; // Reset to the first page
  displayedPrompts = []; // Clear existing prompts
  await loadMorePrompts();
}

// Save cache to localStorage after updating
function updateCache(page, prompts) {
  promptCache[page] = prompts;
  localStorage.setItem('promptCache', JSON.stringify(promptCache));
  localStorage.setItem('cacheTimestamp', Date.now().toString());
}

  function handleScroll() {
    const scrollableElement = document.querySelector('.browse-section');
    if (!scrollableElement) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollableElement;

    // Trigger pre-fetching when the user scrolls near the bottom of the page
    if (scrollHeight - scrollTop - clientHeight <= 100 && hasMorePrompts && !loadingMore) {
      loadMorePrompts(2); // Fetch 2 pages at a time
    }
  }

  // Consolidated cache loading and expiration check logic
onMount(() => {
  const cachedData = localStorage.getItem('promptCache');
  const cacheTimestamp = localStorage.getItem('cacheTimestamp');

  if (cacheTimestamp && Date.now() - parseInt(cacheTimestamp, 10) <= CACHE_EXPIRY) {
    if (cachedData) {
      promptCache = JSON.parse(cachedData);
    }
  } else {
    localStorage.removeItem('promptCache');
    localStorage.removeItem('cacheTimestamp');
    promptCache = {};
  }

    const scrollableElement = document.querySelector('.browse-section');
    if (scrollableElement) {
      scrollableElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollableElement) {
        scrollableElement.removeEventListener('scroll', handleScroll);
      }
    };

});

  let abortController = null; // Controller for aborting redundant requests

  async function loadMorePrompts(batchSize = 1) {
    if (!hasMorePrompts || loadingMore) return; // Prevent duplicate fetches

    console.log(`loadMorePrompts triggered for ${batchSize} pages`);

    loadingMore = true;
    abortController = new AbortController(); // Create a new AbortController
    const signal = abortController.signal;

    try {
      const fetchPromises = [];
      for (let i = 0; i < batchSize; i++) {
        if (!hasMorePrompts || promptCache[currentPage + i]) continue; // Skip if already cached
        fetchPromises.push(
          fetch(`/api/prompts?limit=${pageSize}&page=${currentPage + i}`, { signal })
        );
      }

      const responses = await Promise.all(fetchPromises);
      for (const response of responses) {
        if (!response.ok) throw new Error(response.statusText);
        const data = await response.json();

        promptCache[currentPage] = data.prompts; // Cache the fetched prompts
        displayedPrompts = [...displayedPrompts, ...data.prompts]; // Append new prompts
        prompts = displayedPrompts; // Synchronize prompts with displayedPrompts

        hasMorePrompts = data.prompts.length === pageSize; // Check if more prompts are available
        currentPage++; // Increment the page number
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Fetch aborted');
      } else {
        console.error("Error loading prompts:", error);
      }
    } finally {
      loadingMore = false;
    }
  }



  onMount(async () => {
    // Only load saved prompts if authenticated
    if ($authStore.isAuthenticated) {
      loadSavedPrompts();
    }
    
    await loadInitialPrompts();
    
    // Add click outside listener
    document.addEventListener('click', handleClickOutside);
    
    // Check if we have a pending prompt selection after authentication
    const pendingPromptId = localStorage.getItem('pendingPromptId');
    if (pendingPromptId && $authStore.isAuthenticated) {
      // Clear the pending prompt ID
      localStorage.removeItem('pendingPromptId');
      // Process the prompt selection
      await selectPrompt(pendingPromptId);
      // Navigate to tool page if selection was successful
      if (selectedPrompt) {
        startDataStory();
      }
    }

  });

  // Remove event listener on component destruction
  onDestroy(() => {

    if (abortController) {
      abortController.abort(); // Abort any ongoing requests when the component is destroyed
    }
    document.removeEventListener('click', handleClickOutside);
  });



  async function selectPrompt(promptId, fromBrowse = false) {
    console.log('selectPrompt called with promptId:', promptId); // Log the promptId
    console.log('Current prompts array:', prompts); // Log the prompts array

    if (!promptId) {
      console.warn('No promptId provided to selectPrompt');
      return;
    }

    if (fromBrowse) {
      modalPrompt = displayedPrompts.find(p => p._id.toString() === promptId);
      if (modalPrompt) {
        const bigQuestion = modalPrompt.bigQuestion || 'Untitled Question';
        storyStore.updateBigQuestion(bigQuestion);
        storyStore.updateTemplate('none');
        showDetails = true;
      }
      return;
    }

    // Check cache for the prompt
    let foundPrompt = null;
    for (const page in promptCache) {
      foundPrompt = promptCache[page].find(p => p._id.toString() === promptId);
      if (foundPrompt) break;
    }

    if (!foundPrompt) {
      console.warn('Prompt not found in cache. Fetching from server...');
      try {
        const response = await fetch(`/api/prompts/${promptId}`);
        if (!response.ok) throw new Error('Failed to fetch prompt from server');
        foundPrompt = await response.json();
      } catch (error) {
        console.error('Error fetching prompt:', error);
        return;
      }
    }

    // Only require authentication when starting a data story
    loading = true;
    try {
      console.log('Attempting to select prompt with ID:', promptId);
      await setSelectedPrompt(promptId);
      selectedPrompt = prompts.find(p => p._id.toString() === promptId) || foundPrompt;

      if (!selectedPrompt) {
        console.warn('No prompt found with the provided ID:', promptId);
        return;
      }

      console.log('Selected Prompt Object:', selectedPrompt); // Log the entire selectedPrompt object

      const bigQuestion = selectedPrompt.bigQuestion || 'Untitled Question'; // Fallback for missing BigQuestion
      storyStore.updateBigQuestion(bigQuestion);
      storyStore.updateTemplate('none'); // Reset template, will be selected in grid page
      showDetails = true;
    } catch (error) {
      console.error("Error selecting prompt:", error);
      // Even if there's an error, we can still proceed with the prompt selection
      if (selectedPrompt) {
        const bigQuestion = selectedPrompt.bigQuestion || 'Untitled Question'; // Fallback for missing BigQuestion
        storyStore.updateBigQuestion(bigQuestion);
        storyStore.updateTemplate('none');
        showDetails = true;
      }
    } finally {
      loading = false;
    }
  }

  function startDataStory() {
    // Only check authentication when starting the data story
    if (!$authStore.isAuthenticated) {
      // Store the prompt ID for after authentication
      if (selectedPrompt) {
        localStorage.setItem('pendingPromptId', selectedPrompt._id);
      }
      // Set redirect path to the current page
      authStore.setRedirect(window.location.pathname);
      // Trigger login
      authStore.loginWithGoogle();
      return;
    }
    // Navigate to grid page to select template
    navigate("/grid");
  }

  function selectRandomQuestion() {
    if (prompts.length === 0) return;
    
    // Get random prompt
    const randomPrompt = prompts[Math.floor(Math.random() * prompts.length)];
    
    // Open it in the modal
    selectPrompt(randomPrompt._id.toString(), true);
  }

  function toggleSavePrompt(prompt, event) {
    event.preventDefault();
    event.stopPropagation(); // Ensure the event doesn't bubble up
    
    // If not authenticated, prompt for login
    if (!$authStore.isAuthenticated) {
      authStore.loginWithGoogle();
      return;
    }
    
    const isSaved = savedPrompts.some(p => p._id === prompt._id);
    if (isSaved) {
      removeSavedPrompt(prompt._id);
    } else {
      // Create a clean prompt object with only necessary fields
      const promptToSave = {
        _id: prompt._id,
        bigQuestion: prompt.bigQuestion,
        promptScript: prompt.promptScript,
        topics: prompt.topics
      };
      savePrompt(promptToSave);
    }
  }

  // Add this computed value to track saved state for each prompt
  $: isPromptSaved = (promptId) => savedPrompts.some(p => p._id === promptId);

  async function handleSearch(event) {
    if (event.type === 'click' || (event.type === 'keydown' && event.key === 'Enter')) {
      event.preventDefault();
      hasSubmittedSearch = true;
      searchErrorMessage = '';
      showSearchError = false;
      
      if (!browseSearchQuery.trim()) {
        await loadInitialPrompts();
        return;
      }

      isSearching = true;
      try {
        const searchResults = await apiService.searchPrompts(
          browseSearchQuery.trim()
        );
        displayedPrompts = searchResults;
        hasMorePrompts = false;
      } catch (error) {
        console.error('Search failed:', error);
        displayedPrompts = [];
        searchErrorMessage = 'Search failed. Please try again later.';
        showSearchError = true;
      } finally {
        isSearching = false;
      }
    }
  }


  function resetBrowseFilters() {
    browseSearchQuery = "";
    hasSubmittedSearch = false;
    loadInitialPrompts();
  }

  function closeModal() {
    modalPrompt = null;
  }

function toggleExpandPrompt(promptId) {
  expandedPrompts = { ...expandedPrompts, [promptId]: !expandedPrompts[promptId] };
}

  // Add delete prompt functionality
  async function deletePrompt(prompt) {
    try {
      console.log('Deleting prompt:', prompt._id);
      
      // Call the backend API to delete the prompt
      const response = await fetch(`/api/admin/prompts/${prompt._id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete prompt: ${errorText}`);
      }

      // Update local state
      displayedPrompts = displayedPrompts.filter(p => p._id !== prompt._id);
      
      // If prompt was in saved list, remove it
      if (savedPrompts.some(p => p._id === prompt._id)) {
        removeSavedPrompt(prompt._id);
      }

      // Close the delete confirmation modal
      closeDeleteConfirm();
      
      // Show success message
      alert('Prompt deleted successfully');
    } catch (error) {
      console.error('Error deleting prompt:', error);
      alert('Failed to delete prompt. Please try again.');
    }
  }

  // Add delete confirmation functions
  function openDeleteConfirm(prompt, event) {
    if (event) event.stopPropagation();
    promptToDelete = prompt;
    deleteConfirmOpen = true;
    showMenu = null; // Close menu when opening confirm dialog
  }

  function closeDeleteConfirm() {
    deleteConfirmOpen = false;
    promptToDelete = null;
  }

  // Add update prompt functionality
  async function updatePrompt() {
    try {
      console.log('Updating prompt:', promptToUpdate._id);
      console.log('New data:', updateForm);
      
      // Call the backend API to update the prompt
      const response = await fetch(`/api/admin/prompts/${promptToUpdate._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bigQuestion: updateForm.bigQuestion,
          promptScript: updateForm.promptScript,
          topics: updateForm.topics
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update prompt');
      }
      
      // Update the local state
      displayedPrompts = displayedPrompts.map(item => {
        if (item._id === promptToUpdate._id) {
          return {
            ...item,
            bigQuestion: updateForm.bigQuestion,
            promptScript: updateForm.promptScript,
            topics: updateForm.topics
          };
        }
        return item;
      });
      
      // If prompt was in saved list, update it there too
      promptStore.update(store => {
        const updatedSaved = store.savedPrompts.map(p => {
          if (p._id === promptToUpdate._id) {
            return {
              ...p,
              bigQuestion: updateForm.bigQuestion,
              promptScript: updateForm.promptScript,
              topics: updateForm.topics
            };
          }
          return p;
        });
        
        return {
          ...store,
          savedPrompts: updatedSaved
        };
      });

      // Close the modal and reset form
      closeUpdateModal();
      
      // Show success message
      alert('Prompt updated successfully');
    } catch (error) {
      console.error('Error updating prompt:', error);
      alert(error.message || 'Failed to update prompt');
    }
  }

  // Add update modal functions
  function openUpdateModal(prompt, event) {
    if (event) event.stopPropagation();
    promptToUpdate = prompt;
    updateForm = {
      bigQuestion: prompt.bigQuestion || '',
      promptScript: prompt.promptScript || '',
      topics: prompt.topics || []
    };
    updateModalOpen = true;
    showMenu = null; // Close menu when opening modal
  }

  function closeUpdateModal() {
    updateModalOpen = false;
    promptToUpdate = null;
    updateForm = {
      bigQuestion: '',
      promptScript: '',
      topics: []
    };
  }
</script>

<div class="prompt-selection-container">
  <div class="tabs-container">
    <div class="left-tabs">
      <button 
        class="tab-button" 
        class:active={activeTab === 'browse'}
        on:click={() => activeTab = 'browse'}
      >
        <i class="bi bi-grid"></i>
        Browse
      </button>
      {#if $authStore.isAuthenticated}
        <button 
          class="tab-button" 
          class:active={activeTab === 'saved'}
          on:click={() => activeTab = 'saved'}
        >
          <i class="bi bi-bookmark"></i>
          Saved
        </button>
      {/if}
    </div>
    <button 
      class="tab-button guide-tab" 
      class:active={activeTab === 'guide'}
      on:click={() => activeTab = 'guide'}
    >
      <i class="bi bi-lightbulb"></i>
      Guide Me
    </button>
  </div>

  {#if activeTab === 'saved'}
    {#if !$authStore.isAuthenticated}
      <div class="empty-state" transition:fade={{ duration: 300 }}>
        <div class="empty-state-decoration"></div>
        <div class="empty-state-dots"></div>
        <div class="empty-state-icon" in:scale={{ delay: 300, duration: 400, start: 0.8 }}>
          <i class="bi bi-person-lock"></i>
        </div>
        <h3 class="empty-state-title" in:scale={{ delay: 400, duration: 300, start: 0.9 }}>Sign in required</h3>
        <p class="empty-state-message" in:scale={{ delay: 500, duration: 300, start: 0.9 }}>Please sign in to save and view your prompts</p>
        <button 
          class="empty-state-button"
          on:click={() => authStore.loginWithGoogle()}
          in:scale={{ delay: 600, duration: 300, start: 0.9 }}
        >
          <i class="bi bi-google"></i>
          Sign in with Google
        </button>
      </div>
    {:else if !savedPrompts || savedPrompts.length === 0}
      <div class="empty-state" transition:fade={{ duration: 300 }}>
        <div class="empty-state-decoration"></div>
        <div class="empty-state-dots"></div>
        <div class="empty-state-icon" in:scale={{ delay: 300, duration: 400, start: 0.8 }}>
          <i class="bi bi-bookmark"></i>
        </div>
        <h3 class="empty-state-title" in:scale={{ delay: 400, duration: 300, start: 0.9 }}>No saved prompts yet</h3>
        <p class="empty-state-message" in:scale={{ delay: 500, duration: 300, start: 0.9 }}>Browse and save prompts that interest you</p>
        <button 
          class="empty-state-button"
          on:click={() => activeTab = 'browse'}
          in:scale={{ delay: 600, duration: 300, start: 0.9 }}
        >
          <i class="bi bi-grid"></i>
          Browse prompts
        </button>
      </div>
    {:else}
      <div class="saved-prompts-section">
        {#each savedPrompts as prompt, i}
          {#if prompt && prompt.bigQuestion && prompt.promptScript}
            <div class="prompt-card">
              <div class="question-section" data-index={i % 10}>
                <h3 class="prompt-title">{prompt.bigQuestion}</h3>
              </div>
              
              <div class="prompt-content">
                <div class="content-wrapper">
                  <div class="prompt-preview-container">
                    <p class="prompt-preview" class:expanded={expandedPrompts[prompt._id]}>
                      {prompt.promptScript}
                    </p>
                    {#if prompt.promptScript.length > 200 && !expandedPrompts[prompt._id]}
                      <button 
                        class="expand-button"
                        class:expanded={expandedPrompts[prompt._id]}
                        on:click={(e) => {
                          e.stopPropagation();
                          toggleExpandPrompt(prompt._id);
                        }}
                      >
                        Show more
                      </button>
                    {:else if expandedPrompts[prompt._id]}
                      <button 
                        class="expand-button expanded"
                        on:click={(e) => {
                          e.stopPropagation();
                          toggleExpandPrompt(prompt._id);
                        }}
                      >
                        Show less
                      </button>
                    {/if}
                  </div>

                  <div class="prompt-card-footer">
                    <span class="topic-label">
                      {#if prompt.topics && prompt.topics.length > 0}
                        {typeof prompt.topics[0] === 'string' 
                          ? prompt.topics[0].split('-')
                              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(' ')
                          : prompt.topics[0].topicLabel || 'General Topic'}
                      {:else}
                        General Topic
                      {/if}
                    </span>
                    
                    {#if prompt.similarityScore !== undefined}
                      <span class="similarity-score">
                        <i class="bi bi-lightning-charge"></i>
                        {prompt.similarityScore}% match
                      </span>
                    {/if}
                    
                    <div class="card-footer-buttons">
                      <button 
                        class="save-button-small" 
                        class:saved={isPromptSaved(prompt._id)}
                        on:click={(e) => {
                          e.stopPropagation();
                          toggleSavePrompt(prompt, e);
                        }}
                      >
                        <i class="bi bi-bookmark{isPromptSaved(prompt._id) ? '-fill' : ''}"></i>
                        {isPromptSaved(prompt._id) ? 'Saved' : 'Save'}
                      </button>
                      
                      <button 
                        class="create-story-button-small"
                        on:click={(e) => {
                          e.stopPropagation();
                          selectPrompt(prompt._id);
                          startDataStory();
                        }}
                      >
                        Start data story
                        <i class="bi bi-arrow-right"></i>
                      </button>
                      
                      {#if isAdmin}
                        <div class="prompt-menu">
                          <button 
                            class="menu-button"
                            on:click|stopPropagation={() => showMenu = prompt._id}
                            title="Admin Options"
                          >
                            <i class="bi bi-three-dots-vertical"></i>
                          </button>
                          
                          {#if showMenu === prompt._id}
                            <div class="menu-dropdown">
                              <button 
                                class="menu-item"
                                on:click|stopPropagation={(e) => openUpdateModal(prompt, e)}
                              >
                                <i class="bi bi-pencil"></i>
                                Edit Prompt
                              </button>
                              <button 
                                class="menu-item delete-item"
                                on:click|stopPropagation={(e) => openDeleteConfirm(prompt, e)}
                              >
                                <i class="bi bi-trash"></i>
                                Delete Prompt
                              </button>
                            </div>
                          {/if}
                        </div>
                      {/if}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          {/if}
        {/each}
      </div>
    {/if}
  {:else if activeTab === 'guide'}
    <div class="guide-section">
      <div class="guide-content">
        <div class="guide-iframe-container">
          <iframe 
            src="https://docs.google.com/document/d/e/2PACX-1vRHKFdBAY_ZJ_0NyEQkLx_FheVngR8Ed4Goj2OAqNbLrX-7QuD3BzDNII_rm3NPJD3W-Eilt5M5F7Uj/pub?embedded=true&rm=minimal"
            class="guide-iframe"
            frameborder="0"
            allowfullscreen
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            on:load={() => {
              // Hide loading indicator when iframe loads
              const loadingEl = document.querySelector('.guide-loading');
              if (loadingEl) loadingEl.style.display = 'none';
            }}
            on:error={() => {
              // Show error message if iframe fails to load
              const loadingEl = document.querySelector('.guide-loading');
              if (loadingEl) {
                loadingEl.innerHTML = `
                  <i class="bi bi-exclamation-triangle"></i>
                  <span>Failed to load guide. Please try refreshing the page.</span>
                `;
              }
            }}
          ></iframe>
          <div class="guide-loading">
            <div class="loading-icon-container"><i class="bi bi-arrow-repeat spinner"></i></div>
            <span>Loading guide...</span>
          </div>
        </div>
      </div>
    </div>
  {:else}
    <div class="browse-search-container">
      <div class="filters-row">
        <div class="search-input-wrapper">
          <i class="bi bi-search search-icon"></i>
          <form on:submit|preventDefault={handleSearch}>
            <input
              type="text"
              placeholder="Search prompts..."
              bind:value={browseSearchQuery}
              on:keydown={handleSearch}
              class="search-input"
            />
            <button 
              type="button" 
              class="search-tips-button" 
              title="Search Tips"
              on:click={() => showSearchTips = !showSearchTips}
            >
              <i class="bi bi-question-circle"></i>
            </button>
            {#if browseSearchQuery}
              <button 
                type="button"
                class="clear-button" 
                on:click={resetBrowseFilters}
                aria-label="Clear search"
              >
                <i class="bi bi-x-lg"></i>
              </button>
            {/if}
            <button 
              type="submit" 
              class="search-submit"
              style="display: none;"
            >
              Search
            </button>
          </form>
          {#if showSearchTips}
            <div class="search-tips-dropdown" transition:fade>
              <h4>Search Strategies</h4>
              <div class="search-tips-content">
                <section class="search-tip-section">
                  <h5>Boolean Operators</h5>
                  <ul>
                    <li><code>AND</code>: Match both terms (e.g. <span class="example">"education AND technology"</span>)</li>
                    <li><code>OR</code>: Match either term (e.g. <span class="example">"climate OR weather"</span>)</li>
                    <li><code>NOT</code>: Exclude term (e.g. <span class="example">"politics NOT economy"</span>)</li>
                  </ul>
                </section>

                <section class="search-tip-section">
                  <h5>Exact Phrase Matching</h5>
                  <ul>
                    <li><code>"..."</code>: Match exact phrase (e.g. <span class="example">"public health"</span>)</li>
                  </ul>
                </section>

                <section class="search-tip-section">
                  <h5>Grouping Terms</h5>
                  <ul>
                    <li><code>()</code>: Group terms (e.g. <span class="example">"(education OR learning) AND digital"</span>)</li>
                  </ul>
                </section>

                <section class="search-tip-section">
                  <h5>Wildcards</h5>
                  <ul>
                    <li><code>*</code>: Match any characters (e.g. <span class="example">"environ*"</span> matches environment, environmental, etc.)</li>
                  </ul>
                </section>
              </div>
              <button class="close-tips" on:click={() => showSearchTips = false}>
                <i class="bi bi-x"></i>
              </button>
            </div>
          {/if}
        </div>
      </div>
      
      {#if browseSearchQuery && hasSubmittedSearch}
        <div class="active-filters">
          <span class="filters-label">Search results for: {browseSearchQuery}</span>
          <button 
            class="clear-all-filters"
            on:click={resetBrowseFilters}
          >
            Clear search
          </button>
        </div>
        
        {#if showSearchError}
          <div class="search-error-message" transition:fade={{duration: 300}}>
            <i class="bi bi-exclamation-triangle"></i>
            {searchErrorMessage}
          </div>
        {/if}
      {/if}
    </div>

    <div class="browse-section">
      {#if isSearching}
        <div class="browse-loading">
          <div class="loading-icon-container"><i class="bi bi-arrow-repeat spinner"></i></div>
          <span>Searching prompts...</span>
        </div>
      {:else if browseLoading}
        <div class="browse-loading">
          <div class="loading-icon-container"><i class="bi bi-arrow-repeat spinner"></i></div>
          <span>Loading prompts...</span>
        </div>
      {:else if hasSubmittedSearch && displayedPrompts.length === 0}
        <div class="no-results">
          <i class="bi bi-emoji-frown"></i>
          <p>No prompts found matching "{browseSearchQuery}"</p>
          <button 
            class="clear-search-button"
            on:click={resetBrowseFilters}
          >
            Clear search
          </button>
        </div>
      {:else}
        {#each displayedPrompts as prompt, i}
          <div class="prompt-card">
            <div class="question-section" data-index={i % 10}>
              <h3 class="prompt-title">{prompt.bigQuestion}</h3>
            </div>
            
            <div class="prompt-content">
              <div class="content-wrapper">
                <div class="prompt-preview-container">
                  <p class="prompt-preview" class:expanded={expandedPrompts[prompt._id]}>
                    {prompt.promptScript}
                  </p>
                  {#if prompt.promptScript.length > 200 && !expandedPrompts[prompt._id]}
                    <button 
                      class="expand-button"
                      class:expanded={expandedPrompts[prompt._id]}
                      on:click={(e) => {
                        e.stopPropagation();
                        toggleExpandPrompt(prompt._id);
                      }}
                    >
                      Show more
                    </button>
                  {:else if expandedPrompts[prompt._id]}
                    <button 
                      class="expand-button expanded"
                      on:click={(e) => {
                        e.stopPropagation();
                        toggleExpandPrompt(prompt._id);
                      }}
                    >
                      Show less
                    </button>
                  {/if}
                </div>

                <div class="prompt-card-footer">
                  <span class="topic-label">
                    {#if prompt.topics && prompt.topics.length > 0}
                      {typeof prompt.topics[0] === 'string' 
                        ? prompt.topics[0].split('-')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')
                        : prompt.topics[0].topicLabel || 'General Topic'}
                    {:else}
                      General Topic
                    {/if}
                  </span>
                  
                  {#if prompt.similarityScore !== undefined}
                    <span class="similarity-score">
                      <i class="bi bi-lightning-charge"></i>
                      {prompt.similarityScore}% match
                    </span>
                  {/if}
                  
                  <div class="card-footer-buttons">
                    <button 
                      class="save-button-small" 
                      class:saved={isPromptSaved(prompt._id)}
                      on:click={(e) => {
                        e.stopPropagation();
                        toggleSavePrompt(prompt, e);
                      }}
                    >
                      <i class="bi bi-bookmark{isPromptSaved(prompt._id) ? '-fill' : ''}"></i>
                      {isPromptSaved(prompt._id) ? 'Saved' : 'Save'}
                    </button>
                    
                    <button 
                      class="create-story-button-small"
                      on:click={(e) => {
                        e.stopPropagation();
                        selectPrompt(prompt._id);
                        startDataStory();
                      }}
                    >
                      Start data story
                      <i class="bi bi-arrow-right"></i>
                    </button>
                    
                    {#if isAdmin}
                      <div class="prompt-menu">
                        <button 
                          class="menu-button"
                          on:click|stopPropagation={() => showMenu = prompt._id}
                          title="Admin Options"
                        >
                          <i class="bi bi-three-dots-vertical"></i>
                        </button>
                        
                        {#if showMenu === prompt._id}
                          <div class="menu-dropdown">
                            <button 
                              class="menu-item"
                              on:click|stopPropagation={(e) => openUpdateModal(prompt, e)}
                            >
                              <i class="bi bi-pencil"></i>
                              Edit Prompt
                            </button>
                            <button 
                              class="menu-item delete-item"
                              on:click|stopPropagation={(e) => openDeleteConfirm(prompt, e)}
                            >
                              <i class="bi bi-trash"></i>
                              Delete Prompt
                            </button>
                          </div>
                        {/if}
                      </div>
                    {/if}
                  </div>
                </div>
              </div>
            </div>
          </div>
        {/each}

        {#if hasMorePrompts}
          <button 
            class="load-more-button" 
            on:click={loadMorePrompts}
            disabled={loadingMore || !hasMorePrompts}
          >
            {#if loadingMore}
              <div class="loading-icon-container"><i class="bi bi-arrow-repeat spinner"></i></div>
            {:else}
              Load more prompts
            {/if}
          </button>
        {/if}
      {/if}
    </div>
  {/if}

  {#if loading}
    <div class="loading">
      <div class="loading-icon-container"><i class="bi bi-arrow-repeat spinner"></i></div> Loading...
    </div>
  {/if}

  {#if showDetails && selectedPrompt}
    <div class="details-container" transition:fade>
      <div class="details-card">
        <div class="details-header">
          <h2>{selectedPrompt.bigQuestion}</h2>
          <span class="label">
            {#if selectedPrompt.topics && selectedPrompt.topics.length > 0}
              {typeof selectedPrompt.topics[0] === 'string' 
                ? selectedPrompt.topics[0].split('-')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')
                : selectedPrompt.topics[0].topicLabel || 'General Topic'}
            {:else}
              General Topic
            {/if}
          </span>
        </div>
        
        <div class="prompt-text">
          <h3>About this question:</h3>
          <p>{selectedPrompt.promptScript}</p>
        </div>

        <div class="action-section">
          <div class="info-box">
            <div class="info-content">
              <i class="bi bi-filter-circle"></i>
              <p>This question will help focus your exploration on relevant data for your story</p>
            </div>
          </div>

          <div class="action-buttons">
            <button 
              class="save-button" 
              class:saved={isPromptSaved(selectedPrompt._id)}
              on:click={(e) => toggleSavePrompt(selectedPrompt, e)}
            >
              <i class="bi bi-bookmark{isPromptSaved(selectedPrompt._id) ? '-fill' : ''}"></i>
              {isPromptSaved(selectedPrompt._id) ? 'Saved' : 'Save'}
            </button>
            <button class="create-story-button" on:click={startDataStory}>
              Start data story
              <i class="bi bi-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  {/if}

  {#if modalPrompt}
    <div class="modal-backdrop" on:click={closeModal} transition:fade>
      <div 
        class="modal-content"
        on:click|stopPropagation
        transition:scale={{duration: 200, start: 0.95}}
      >
        <div class="modal-header">
          <span class="topic-label">
            {#if modalPrompt.topics && modalPrompt.topics.length > 0}
              {typeof modalPrompt.topics[0] === 'string'
                ? modalPrompt.topics[0].split('-')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')
                : modalPrompt.topics[0].topicLabel || 'General Topic'}
            {:else}
              General Topic
            {/if}
          </span>
          <button class="close-button" on:click={closeModal}>
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <h2 class="modal-title">{modalPrompt.bigQuestion}</h2>
        
        <div class="modal-body">
          <p class="modal-text">{modalPrompt.promptScript}</p>
        </div>

        <div class="modal-footer">
          <button 
            class="save-button" 
            class:saved={savedPrompts.some(p => p._id === modalPrompt._id)}
            on:click={(e) => toggleSavePrompt(modalPrompt, e)}
          >
            <i class="bi bi-bookmark{savedPrompts.some(p => p._id === modalPrompt._id) ? '-fill' : ''}"></i>
            {savedPrompts.some(p => p._id === modalPrompt._id) ? 'Saved' : 'Save'}
          </button>
          <button 
            class="create-story-button" 
            on:click={() => {
              selectPrompt(modalPrompt._id);
              startDataStory();
            }}
          >
            Start data story
            <i class="bi bi-arrow-right"></i>
          </button>
        </div>
      </div>
    </div>
  {/if}

  {#if deleteConfirmOpen}
    <div 
      class="modal-backdrop" 
      transition:fade 
      on:click={closeDeleteConfirm}
    >
      <div 
        class="modal-container delete-confirm"
        on:click|stopPropagation
      >
        <div class="delete-confirm-content">
          <h3>Delete Prompt</h3>
          <p>Are you sure you want to delete this prompt? This action cannot be undone.</p>
          <div class="delete-confirm-actions">
            <button 
              class="cancel-button"
              on:click={closeDeleteConfirm}
            >
              Cancel
            </button>
            <button 
              class="delete-button"
              on:click={() => deletePrompt(promptToDelete)}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  {/if}

  {#if updateModalOpen}
    <div 
      class="modal-backdrop" 
      transition:fade 
      on:click={closeUpdateModal}
    >
      <div 
        class="modal-container update-modal"
        on:click|stopPropagation
      >
        <div class="update-form">
          <h3>Edit Prompt</h3>
          <div class="form-group">
            <label for="bigQuestion">Question</label>
            <input 
              type="text" 
              id="bigQuestion"
              bind:value={updateForm.bigQuestion}
              placeholder="Enter big question"
            />
          </div>
          <div class="form-group">
            <label for="promptScript">Prompt Script</label>
            <textarea 
              id="promptScript"
              bind:value={updateForm.promptScript}
              placeholder="Enter prompt script"
              rows="6"
            ></textarea>
          </div>
          <div class="form-group">
            <label for="topics">Topics</label>
            <p class="form-hint">Topics are displayed in JSON format and can't be edited directly here.</p>
          </div>
          <div class="form-actions">
            <button 
              class="cancel-button"
              on:click={closeUpdateModal}
            >
              Cancel
            </button>
            <button 
              class="update-button"
              on:click={() => updatePrompt()}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .prompt-selection-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
    overflow: hidden;
  }

  .search-section {
    flex-shrink: 0;
    background: white;
    padding: 1rem;
    border-radius: 8px;
    position: relative;
    z-index: 10;
  }

  .search-container {
    position: relative;
    width: 100%;
  }

  .search-input-wrapper {
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
  }
  
  .search-input-wrapper form {
    width: 100%;
    position: relative;
    margin: 0;
  }
  
  .search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 0.875rem;
    pointer-events: none;
    z-index: 2;
  }

  .search-input {
    width: 100%;
    padding: 0.875rem 1rem 0.875rem 2.5rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 1rem;
    transition: all 0.2s;
    line-height: 1.5;
  }

  .search-input:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  .search-tips-button {
    position: absolute;
    right: 2.5rem;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #94a3b8;
    padding: 0.25rem;
    cursor: pointer;
    transition: color 0.2s;
    z-index: 1;
  }

  .search-tips-button:hover {
    color: #2563eb;
  }

  .clear-button {
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background-color 0.2s;
  }

  .clear-button i {
    font-size: 0.875rem;
  }

  .clear-button:hover {
    background-color: #f1f5f9;
    color: #475569;
  }

  .results-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 0.5rem;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    z-index: 20;
    max-height: min(400px, 60vh);
    overflow-y: auto;
    margin: 0.5rem;
    width: calc(100% - 1rem);
  }

  .results-dropdown::-webkit-scrollbar {
    width: 6px;
  }

  .results-dropdown::-webkit-scrollbar-track {
    background: transparent;
  }

  .results-dropdown::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 3px;
  }

  .results-dropdown::-webkit-scrollbar-thumb:hover {
    background-color: #94a3b8;
  }

  .result-item {
    width: 100%;
    padding: 0.75rem 1rem;
    border: none;
    background: none;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    cursor: pointer;
    transition: background 0.2s;
  }

  .result-item:hover {
    background: #f8fafc;
  }

  .result-icon {
    color: #666;
    font-size: 0.875rem;
  }

  .details-container {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 0 1rem;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    will-change: transform;
  }

  .details-card {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    border: 1px solid #e5e7eb;
    margin-bottom: 1rem;
    display: grid;
    grid-template-rows: auto 1fr auto;
    gap: 1.5rem;
  }

  .details-header {
    flex-shrink: 0;
  }

  .details-header h2 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 0.5rem;
  }

  .label {
    display: inline-block;
    padding: 0.25rem 0.75rem;
    background: #f0f9ff;
    color: #0369a1;
    border-radius: 1rem;
    font-size: 0.875rem;
    font-weight: 500;
  }

  .action-section {
    margin-top: auto;
    padding-top: 1.5rem;
    border-top: 1px solid #e5e7eb;
  }

  .info-box {
    background: #f8fafc;
    border-radius: 12px;
    padding: 1rem 1.25rem;
  }

  .info-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .info-icon {
    color: #64748b;
    font-size: 1.25rem;
  }

  .info-content p {
    color: #64748b;
    font-size: 0.875rem;
    margin: 0;
    line-height: 1.5;
  }

  .prompt-text {
    overflow-wrap: break-word;
  }

  .prompt-text h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.5rem;
  }

  .prompt-text p {
    color: #4b5563;
    line-height: 1.6;
  }

  .create-story-button {
    width: 100%;
    padding: 1rem;
    background: #2563eb;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
    letter-spacing: 0.01em;
  }

  .create-story-button:hover {
    background: #1d4ed8;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(37, 99, 235, 0.1);
  }

  .create-story-button i {
    transition: transform 0.2s ease;
  }

  .create-story-button:hover i {
    transform: translateX(4px);
  }

  .loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: white;
    padding: 0.75rem 1.5rem;
    border-radius: 2rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    gap: 0.5rem;
    z-index: 30;
  }

  .spinner {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
  }

  .search-hint {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    margin-bottom: 0.75rem;
    background: #f8fafc;
    border-radius: 8px;
    color: #64748b;
    font-size: 0.875rem;
  }

  .search-hint i {
    color: #2563eb;
    font-size: 1rem;
  }

  .random-link-container {
    display: flex;
    justify-content: flex-end;
    margin-top: 0.5rem;
  }

  .random-link {
    background: none;
    border: none;
    color: #6b7280;
    font-size: 0.875rem;
    padding: 0;
    cursor: pointer;
    transition: color 0.2s ease;
  }

  .random-link:hover {
    color: #2563eb;
    text-decoration: underline;
  }

  .tabs-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    background: white;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  .left-tabs {
    display: flex;
    gap: 1rem;
  }

  .tab-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border: none;
    background: none;
    color: #64748b;
    font-size: 0.875rem;
    font-weight: 500;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
  }

  .tab-button.active {
    color: #2563eb;
    border-bottom-color: #2563eb;
    background: #eff6ff;
  }

  .tab-button:hover {
    color: #2563eb;
    background: #f8fafc;
  }

  .guide-tab {
    font-size: 0.8125rem;
    padding: 0.5rem 0.75rem;
    background: #f8fafc;
    border-radius: 6px;
    border-bottom: none;
  }

  .guide-tab:hover {
    background: #f1f5f9;
    color: #2563eb;
  }

  .guide-tab.active {
    background: #eff6ff;
    color: #2563eb;
    font-weight: 600;
  }

  .browse-section {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    padding: 1.5rem;
    overflow-y: auto;
    height: calc(100vh - 200px);
    position: relative;
    align-items: center;
    width: 100%;
  }

  /* Add this new style for saved prompts section */
  .saved-prompts-section {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    padding: 1.5rem;
    overflow-y: auto;
    height: calc(100vh - 200px);
    position: relative;
    align-items: center;
    width: 100%;
  }

  .prompt-card {
    background: white;
    border-radius: 8px;
    border: 1px solid #dbdbdb;
    transition: all 0.2s ease;
    width: 100%;
    max-width: 470px;
    display: flex;
    flex-direction: column;
    position: relative;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  .prompt-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  /* Question section that acts like an image */
  .question-section {
    width: 100%;
    min-height: 220px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    text-align: center;
    position: relative;
    overflow: hidden;
    background-size: cover;
    background-position: center;
  }

  /* Gradient backgrounds */
  .question-section[data-index="0"] { background: linear-gradient(135deg, #FF6B6B, #FF8E8E); }
  .question-section[data-index="1"] { background: linear-gradient(135deg, #4ECDC4, #45B7AF); }
  .question-section[data-index="2"] { background: linear-gradient(135deg, #96CEB4, #FFEEAD); }
  .question-section[data-index="3"] { background: linear-gradient(135deg, #D4A5A5, #9B59B6); }
  .question-section[data-index="4"] { background: linear-gradient(135deg, #3498DB, #2980B9); }
  .question-section[data-index="5"] { background: linear-gradient(135deg, #E67E22, #D35400); }
  .question-section[data-index="6"] { background: linear-gradient(135deg, #1ABC9C, #16A085); }
  .question-section[data-index="7"] { background: linear-gradient(135deg, #F1C40F, #F39C12); }
  .question-section[data-index="8"] { background: linear-gradient(135deg, #9B59B6, #8E44AD); }
  .question-section[data-index="9"] { background: linear-gradient(135deg, #34495E, #2C3E50); }

  .prompt-title {
    font-size: 1.75rem;
    font-weight: 600;
    color: white;
    line-height: 1.3;
    margin: 0;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
    position: relative;
    z-index: 1;
    max-width: 90%;
    margin: 0 auto;
  }

  /* Content section below the question */
  .prompt-content {
    padding: 1.25rem 1.5rem;
    background: white;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 200px;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  .prompt-preview-container {
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .prompt-preview {
    color: #262626;
    font-size: 0.9375rem;
    line-height: 1.6;
    margin: 0;
    transition: all 0.3s ease;
  }

  .prompt-preview.expanded {
    max-height: none;
  }

  .prompt-preview:not(.expanded) {
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
    position: relative;
  }

  .expand-button {
    background: none;
    border: none;
    color: #2563eb;
    font-size: 0.875rem;
    font-weight: 500;
    padding: 0.5rem 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    transition: all 0.2s ease;
    margin-top: 0.5rem;
  }

  .expand-button:hover {
    color: #1d4ed8;
  }

  .expand-button::after {
    content: '';
    display: inline-block;
    width: 12px;
    height: 12px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
    transition: transform 0.2s ease;
  }

  .expand-button:not(.expanded)::after {
    transform: rotate(-90deg);
  }

  .expand-button.expanded::after {
    transform: rotate(180deg);
  }

  .prompt-card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 1.25rem;
    margin-top: 1.25rem;
    border-top: 1px solid #dbdbdb;
    background: white;
  }

  .topic-label {
    font-size: 0.75rem;
    color: #8e8e8e;
    background: #fafafa;
    padding: 0.375rem 0.875rem;
    border-radius: 1rem;
    font-weight: 500;
  }

  .card-footer-buttons {
    display: flex;
    gap: 0.75rem;
  }

  .save-button-small {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1rem;
    background: white;
    border: 1px solid #dbdbdb;
    border-radius: 6px;
    color: #8e8e8e;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    height: 36px;
    position: relative;
  }

  .save-button-small:hover {
    background: #fafafa;
    border-color: #c7c7c7;
    transform: translateY(-1px);
  }

  .save-button-small:active {
    transform: translateY(0);
  }

  .save-button-small.saved {
    color: #2563eb;
    border-color: #2563eb;
    background: #f0f7ff;
    box-shadow: 0 2px 4px rgba(37, 99, 235, 0.1);
  }

  .save-button-small.saved:hover {
    background: #e0f2ff;
    border-color: #1d4ed8;
    color: #1d4ed8;
  }

  .save-button-small i {
    transition: transform 0.2s ease;
  }

  .save-button-small:hover i {
    transform: scale(1.1);
  }

  .save-button-small.saved i {
    color: #2563eb;
  }

  .create-story-button-small {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1rem;
    background: #0095f6;
    border: none;
    border-radius: 6px;
    color: white;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    height: 36px;
    white-space: nowrap;
  }

  .create-story-button-small:hover {
    background: #0081d6;
  }

  .create-story-button-small i {
    transition: transform 0.2s ease;
  }

  .create-story-button-small:hover i {
    transform: translateX(2px);
  }

  /* Loading states */
  .browse-loading {
    width: 100%;
    max-width: 470px;
    padding: 2rem;
    text-align: center;
    background: white;
    border-radius: 8px;
    border: 1px solid #dbdbdb;
    color: #8e8e8e;
  }

  .no-results {
    width: 100%;
    max-width: 470px;
    padding: 3rem 2rem;
    text-align: center;
    background: white;
    border-radius: 8px;
    border: 1px solid #dbdbdb;
    color: #8e8e8e;
  }

  .no-results i {
    font-size: 2rem;
  }

  .no-results p {
    margin: 0;
    font-size: 0.875rem;
  }

  .clear-search-button {
    padding: 0.5rem 1rem;
    background: #2563eb;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .clear-search-button:hover {
    background: #1d4ed8;
  }

  .load-more-button {
    margin: 1rem auto;
    padding: 0.75rem 2rem;
    background: white;
    border: 1px solid #dbdbdb;
    border-radius: 6px;
    color: #262626;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    max-width: 470px;
    justify-content: center;
  }

  .load-more-button:hover {
    background: #fafafa;
    border-color: #c7c7c7;
  }

  .load-more-button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
  }
  
  .modal-container {
    background: white;
    border-radius: 12px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }
  
  .modal-content {
    background: white;
    border-radius: 12px;
    width: 100%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
    padding: 1.5rem;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }
  
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .modal-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    line-height: 1.4;
    margin: 0;
  }
  
  .modal-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }
  
  .modal-text {
    color: #4b5563;
    line-height: 1.6;
    margin: 0;
  }
  
  .modal-footer {
    display: flex;
    gap: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e5e7eb;
  }
  
  .close-button {
    background: none;
    border: none;
    color: #6b7280;
    padding: 0.5rem;
    cursor: pointer;
    transition: color 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .close-button:hover {
    color: #111827;
  }
  
  .delete-confirm {
    max-width: 400px;
    padding: 24px;
  }

  .update-modal {
    max-width: 500px;
  }

  .update-form {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 24px;
  }
  
  .update-form h3 {
    margin: 0;
    color: #1e293b;
    font-size: 1.25rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .form-group label {
    font-weight: 500;
    color: #475569;
    font-size: 0.875rem;
  }

  .form-hint {
    font-size: 0.75rem;
    color: #64748b;
    margin: 0;
    line-height: 1.4;
  }

  .form-group input,
  .form-group textarea {
    padding: 8px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 0.875rem;
    transition: all 0.2s ease;
  }

  .form-group input:focus,
  .form-group textarea:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }

  .form-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 8px;
  }

  .update-button {
    padding: 8px 16px;
    background: #2563eb;
    border: none;
    border-radius: 6px;
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .update-button:hover {
    background: #1d4ed8;
  }

  /* Admin menu styles */
  .prompt-menu {
    position: relative;
    z-index: 100;
  }

  .menu-button {
    background: none;
    border: none;
    font-size: 1rem;
    cursor: pointer;
    padding: 4px 8px;
    color: #64748b;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .menu-button:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #333;
  }

  .menu-dropdown {
    position: absolute;
    right: 0;
    top: 100%;
    background: white;
    border: 1px solid #ddd;
    border-radius: 6px;
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    z-index: 101;
    min-width: 160px;
    margin-top: 4px;
  }

  .menu-item {
    width: 100%;
    padding: 8px 16px;
    border: none;
    background: none;
    text-align: left;
    cursor: pointer;
    white-space: nowrap;
    color: #333;
    font-size: 14px;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .menu-item:hover {
    background: #f5f5f5;
  }
  
  .delete-item {
    color: #ef4444;
  }

  .delete-item:hover {
    background: #fee2e2;
  }
  
  /* Delete confirmation modal */
  .delete-confirm-content {
    text-align: center;
  }

  .delete-confirm-content h3 {
    margin: 0 0 16px;
    color: #1e293b;
  }

  .delete-confirm-content p {
    margin: 0 0 24px;
    color: #64748b;
  }

  .delete-confirm-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
  }

  .cancel-button {
    padding: 8px 16px;
    background: #f1f5f9;
    border: none;
    border-radius: 6px;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .cancel-button:hover {
    background: #e2e8f0;
  }

  .delete-button {
    padding: 8px 16px;
    background: #ef4444;
    border: none;
    border-radius: 6px;
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .delete-button:hover {
    background: #dc2626;
  }

  /* Browse search container and filters */
  .browse-search-container {
    padding: 0 1rem;
    margin-bottom: 1rem;
  }
  
  .browse-search-container .search-input-wrapper {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 0.25rem;
  }
  
  .browse-search-container .search-input {
    border: none;
    padding: 0.75rem 1rem 0.75rem 2.5rem;
  }
  
  .browse-search-container .search-input:focus {
    box-shadow: none;
    border: none;
    outline: none;
  }
  
  .filters-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    width: 100%;
  }
  
  .search-input-wrapper {
    position: relative;
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0; /* Prevent flex item from overflowing */
  }
  
  .search-input-wrapper form {
    width: 100%;
    position: relative;
    margin: 0;
  }
  
  .active-filters {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-top: 0.75rem;
    flex-wrap: wrap;
  }
  
  .filters-label {
    color: #6b7280;
    font-size: 0.875rem;
  }
  
  .clear-all-filters {
    background: none;
    border: none;
    color: #6b7280;
    font-size: 0.875rem;
    padding: 0.25rem 0.5rem;
    cursor: pointer;
    text-decoration: underline;
  }
  
  .clear-all-filters:hover {
    color: #ef4444;
  }
  
  @media (max-width: 768px) {
    .filters-row {
      flex-direction: column;
      gap: 0.5rem;
    }
    
    .search-type-toggle {
      margin-left: 0;
      width: 100%;
      justify-content: space-between;
    }
    
    .search-type-button {
      flex: 1;
      justify-content: center;
    }
    
    .search-type-label {
      margin-top: 0.25rem;
    }
  }
</style>
